import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { Input, StackField } from "@/components/ui/form";
import { Notice } from "@/components/ui/notice";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useRefreshMe } from "@/lib/session";

/** Only same-site paths are followed after sign-in. */
export const safeNext = (v: string | null) => (v && v.startsWith("/") && !v.startsWith("//") ? v : "/");

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refresh = useRefreshMe();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shown, setShown] = useState(false);
  const next = safeNext(params.get("next"));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { data, error } = await authClient.signIn.email({ email, password });
    setLoading(false);
    if (error) return setError(authErrorMessage(error));
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) return navigate(`/login/2fa?next=${encodeURIComponent(next)}`);
    await refresh();
    navigate(next, { replace: true });
  };

  return (
    <AuthLayout title="ログイン" lead="招待メールで設定したメールアドレスとパスワードを入力してください。">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {params.get("reset") === "1" && <Notice tone="ok">パスワードを変更しました。新しいパスワードでログインしてください。</Notice>}
        {error && <Notice tone="bad">{error}</Notice>}
        <StackField label="メールアドレス">{(id) => <Input id={id} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 text-[15px]" />}</StackField>
        <StackField label="パスワード">
          {(id) => (
            <div className="relative">
              <Input id={id} type={shown ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 pr-11 text-[15px]" />
              <button type="button" onClick={() => setShown((v) => !v)} className="absolute top-1/2 right-1.5 flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted hover:bg-ground hover:text-ink" aria-label={shown ? "パスワードを隠す" : "パスワードを表示する"} aria-pressed={shown}>
                {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          )}
        </StackField>
        <Button type="submit" size="lg" className="group w-full" loading={loading} disabled={!email || !password}>
          ログイン
          <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
        </Button>
        <p className="text-center text-[13px]">
          <Link to="/forgot-password" className="text-brand underline-offset-4 hover:underline">
            パスワードをお忘れの方
          </Link>
        </p>
      </form>
      <p className="mt-6 rounded-lg bg-ground px-4 py-3 text-center text-[13px] text-ink-2">
        はじめての方へ：
        <Link to="/about" className="ml-1 inline-flex items-center gap-0.5 font-bold text-brand underline-offset-4 hover:underline">
          サービスの紹介を見る
          <ArrowRight className="size-3.5" />
        </Link>
      </p>
    </AuthLayout>
  );
}
